import { useEffect, useRef } from "react"
import { IconAlertTriangle, IconCircleCheck, IconClock, IconLoader2 } from "@tabler/icons-react"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import type { BookingMoveBatch, BookingMoveBatchItemStatus } from "../schedule-overrides/scheduleOverridesApi"
import { batchProgressLabel, type BookingMoveBatchState, decodeBatchStatus } from "../schedule-overrides/lib/bookingMoveBatch"
import { useBookingMoveBatch } from "../schedule-overrides/lib/useBookingMoveBatch"
import { formatInstant } from "./automaticReassignment"

const STATE_LABEL: Record<BookingMoveBatchState, string> = {
  queued: "Waiting to start",
  running: "Moving appointments…",
  succeeded: "Finished",
  failed: "Stopped",
}

const ITEM_LABEL: Record<BookingMoveBatchItemStatus, string> = {
  0: "Waiting",
  1: "Moved",
  2: "Not moved: sent to the review queue",
}

/**
 * A booking-move batch's progress and result (AB#809/AB#810), as drawn from one status record: its
 * state, the moved / review-queue / waiting counts, and one row per booking. `names` maps a Cal booking
 * uid to the customer's name where the page knows it. Pure: BookingMoveBatchProgress feeds it.
 */
export function BookingMoveBatchProgressView({
  batch,
  names,
  notFound = false,
  error,
}: {
  batch: BookingMoveBatch | undefined
  names?: ReadonlyMap<string, string>
  notFound?: boolean
  error?: unknown
}) {
  if (notFound) {
    return <p className="text-sm text-muted-foreground" role="status">This reassignment could not be found.</p>
  }
  if (!batch) {
    return error ? (
      <p className="text-sm text-destructive" role="alert">Its progress could not be read. It carries on; check Booking Review later.</p>
    ) : (
      <div className="flex items-center gap-2 text-sm text-muted-foreground" role="status">
        <IconLoader2 className="size-4 animate-spin" />
        Reading its progress…
      </div>
    )
  }

  const state = decodeBatchStatus(batch.status) ?? "queued"
  const finished = state === "succeeded" || state === "failed"

  return (
    <div className="flex flex-col gap-3" data-testid="booking-move-batch-progress" data-batch-status={state}>
      <div className="flex items-center gap-2 text-sm font-medium" role="status">
        {finished ? (
          state === "failed" || batch.failedCount > 0
            ? <IconAlertTriangle className="size-4 text-amber-600" />
            : <IconCircleCheck className="size-4 text-emerald-600" />
        ) : (
          <IconLoader2 className="size-4 animate-spin" />
        )}
        <span>{STATE_LABEL[state]}</span>
        <span className="font-normal text-muted-foreground">· {batchProgressLabel(batch)}</span>
      </div>

      <dl className="grid grid-cols-3 gap-2 text-center text-sm">
        <div className="rounded-md border p-2">
          <dt className="text-xs text-muted-foreground">Moved</dt>
          <dd className="font-semibold" data-count="moved">{batch.movedCount}</dd>
        </div>
        <div className="rounded-md border p-2">
          <dt className="text-xs text-muted-foreground">Sent to review</dt>
          <dd className="font-semibold" data-count="failed">{batch.failedCount}</dd>
        </div>
        <div className="rounded-md border p-2">
          <dt className="text-xs text-muted-foreground">Waiting</dt>
          <dd className="font-semibold" data-count="pending">{batch.pendingCount}</dd>
        </div>
      </dl>

      {batch.failedCount > 0 && (
        <p className="text-xs text-muted-foreground">
          Appointments not moved stay booked where they were and are waiting in Appointments → Booking Review.
        </p>
      )}

      {batch.items.length > 0 && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Appointment</TableHead>
              <TableHead>New time</TableHead>
              <TableHead>Result</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {batch.items.map(item => (
              <TableRow key={item.id} data-batch-item={item.status}>
                <TableCell>{(item.calBookingUid && names?.get(item.calBookingUid)) || "Appointment"}</TableCell>
                <TableCell className="whitespace-nowrap">
                  {item.status === 2 ? (
                    <div className="text-muted-foreground">—</div>
                  ) : (
                    <>
                      <div>{formatInstant(item.targetStart)}</div>
                      {item.targetHostEmail && <div className="text-xs text-muted-foreground">{item.targetHostEmail}</div>}
                    </>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    {item.status === 0 && <IconClock className="size-4 text-muted-foreground" />}
                    {item.status === 1 && <IconCircleCheck className="size-4 text-emerald-600" />}
                    {item.status === 2 && <IconAlertTriangle className="size-4 text-amber-600" />}
                    {ITEM_LABEL[item.status]}
                  </div>
                  {item.status === 2 && item.failureDetail && (
                    <div className="text-xs text-muted-foreground">{item.failureDetail}</div>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  )
}

/**
 * A booking-move batch followed live (AB#809/AB#810; reusable by the manual workspace, SU10): polled by
 * useBookingMoveBatch until it finishes — the "booking-move-batch" notification ends the wait at once —
 * then `onFinished` hears the final record, once. The hook drops the bookings and overrides from the
 * cache when it finishes, so the override's detail and the week are read again.
 */
export function BookingMoveBatchProgress({
  batchId,
  names,
  onFinished,
  pollMs,
}: {
  batchId: string
  names?: ReadonlyMap<string, string>
  onFinished?: (batch: BookingMoveBatch) => void
  /** For tests; the hook's default otherwise. */
  pollMs?: number
}) {
  const { batch, status, notFound, error } = useBookingMoveBatch(batchId, pollMs)
  const told = useRef<string | null>(null)
  const finished = status === "succeeded" || status === "failed"

  useEffect(() => {
    if (!finished || !batch || told.current === batch.id) return
    told.current = batch.id
    onFinished?.(batch)
  }, [finished, batch, onFinished])

  return <BookingMoveBatchProgressView batch={batch} names={names} notFound={notFound} error={error} />
}
